import { logSafeError } from "@smartmanager/shell-ui/shared/logging/safeError";
/**
 * Platform Detection Utilities
 * 
 * Use these to conditionally render components or execute code
 * based on the runtime environment.
 */

// Check if code is running in browser (client-side)
export const isBrowser = globalThis.window !== undefined;

// Check if running in Electron
export const isElectron = isBrowser && !!(globalThis as any).electronAPI?.isElectron;

// Check if running on Vercel (server-side check)
export const isVercel = process.env.VERCEL === '1';

// Check if in development mode
export const isDevelopment = process.env.NEXT_PUBLIC_ENV === 'development';

// Check if in production mode  
export const isProduction = process.env.NEXT_PUBLIC_ENV === 'production';

// Get current platform (client-side only)
export function getPlatform(): 'electron' | 'web' | 'server' {
  if (!isBrowser) return 'server';
  if (isElectron) return 'electron';
  return 'web';
}

// Folder watcher types (matching global types in types/globals.d.ts)
export interface WatchedFileInfo {
  name: string;
  path: string;
  size: number;
  lastModified: number;
  base64: string;
  mimeType: string;
}

export interface FolderWatcherState {
  isWatching: boolean;
  folderPath: string | null;
  savedFolder: string | null;
  autoStart: boolean;
  /** Files handed to the app today (older desktop builds omit it). */
  importedToday?: number;
  /** When the folder was last read, in ms. */
  lastScanAt?: number | null;
}

export interface FolderWatcherError {
  message: string;
}

export interface AuthPopupResult {
  success: boolean;
  session?: string;
  error?: string;
}

export interface EmbeddedAuthState {
  status: 'loading' | 'ready' | 'finishing' | 'error';
  hostname?: string;
  error?: string;
}

/**
 * Get the Electron API object (only available in Electron)
 * Note: Uses runtime check instead of the `isElectron` constant because
 * the constant may be evaluated before window.electronAPI is set up
 */
export function getElectronAPI() {
  // Runtime check - don't use `isElectron` constant as it may be stale
  if (typeof window === 'undefined') return null;
  if (!window.electronAPI?.isElectron) return null;
  return window.electronAPI;
}

/**
 * Managed Smart Manager OS owns the full-screen shell lifecycle, so exposing
 * desktop minimize/close actions there would strand the kiosk session. Other
 * frameless Electron windows retain their accessible native-window controls.
 */
export function shouldShowDesktopWindowControls(
  api: Pick<NonNullable<Window["electronAPI"]>, "isElectron" | "isManagedOs"> | null | undefined,
): boolean {
  return Boolean(api?.isElectron && !api.isManagedOs);
}

/**
 * Get Electron app info (only works in Electron)
 */
export async function getElectronInfo() {
  const api = getElectronAPI();
  if (!api) return null;
  
  return {
    version: await api.getAppVersion(),
    platform: await api.getPlatform(),
    isPackaged: await api.isPackaged(),
  };
}

/**
 * Minimize the Electron window that owns the current renderer.
 */
export async function minimizeDesktopWindow(): Promise<boolean> {
  const api = getElectronAPI();
  if (!api) return false;

  try {
    return await api.minimizeCurrentWindow();
  } catch (err) {
    console.error("Failed to minimize desktop window:", logSafeError(err));
    return false;
  }
}

/**
 * Close the Electron window that owns the current renderer.
 */
export async function closeDesktopWindow(): Promise<boolean> {
  const api = getElectronAPI();
  if (!api) return false;

  try {
    return await api.closeCurrentWindow();
  } catch (err) {
    console.error("Failed to close desktop window:", logSafeError(err));
    return false;
  }
}

/**
 * Apply the OS title-bar double-click action (zoom, minimize or nothing on
 * macOS, per System Settings; maximize elsewhere) to the window that owns
 * this renderer.
 */
export async function titleBarDoubleClickDesktopWindow(): Promise<boolean> {
  const api = getElectronAPI();
  if (!api?.titleBarDoubleClickCurrentWindow) return false;

  try {
    return await api.titleBarDoubleClickCurrentWindow();
  } catch (err) {
    console.error("Failed to apply desktop title-bar double-click:", logSafeError(err));
    return false;
  }
}

type DesktopElectronAPI = NonNullable<Window["electronAPI"]>;

function sendDesktopWindowDrag(send: (api: DesktopElectronAPI) => void): boolean {
  const api = getElectronAPI();
  if (!api) return false;

  try {
    send(api);
    return true;
  } catch (err) {
    console.error("Failed to drag desktop window:", logSafeError(err));
    return false;
  }
}

/**
 * The rail's drag of the Electron window that owns this renderer. Main moves
 * the window and, like a native title-bar drag, unzooms it when the drag
 * starts and fits it to the display it is dropped on (electron/windowDrag.js).
 */
export function startDesktopWindowDrag(start: DesktopWindowDragStart): boolean {
  return sendDesktopWindowDrag((api) => api.startCurrentWindowDrag(start));
}

export function dragDesktopWindow(pointer: DesktopWindowDragPointer): boolean {
  return sendDesktopWindowDrag((api) => api.dragCurrentWindow(pointer));
}

export function endDesktopWindowDrag(pointer: DesktopWindowDragPointer): boolean {
  return sendDesktopWindowDrag((api) => api.endCurrentWindowDrag(pointer));
}

export const NO_FREE_DESKTOP_DISPLAYS: DesktopDisplayAvailability = Object.freeze({
  displays: [],
  freeDisplayIds: [],
});

/**
 * Displays that have no Smart Manager window yet. Web, mobile and older
 * desktop shells report none.
 */
export async function getFreeDesktopDisplays(): Promise<DesktopDisplayAvailability> {
  const api = getElectronAPI();
  if (!api?.getFreeDisplays) return NO_FREE_DESKTOP_DISPLAYS;

  try {
    return await api.getFreeDisplays();
  } catch (err) {
    console.error("Failed to read free desktop displays:", logSafeError(err));
    return NO_FREE_DESKTOP_DISPLAYS;
  }
}

/**
 * Calls back whenever the main process reports a change in which displays are
 * free. Returns the unsubscribe function; a no-op off the desktop.
 */
export function subscribeToFreeDesktopDisplays(
  callback: (state: DesktopDisplayAvailability) => void,
): () => void {
  const api = getElectronAPI();
  if (!api?.onFreeDisplaysChanged) return () => undefined;

  try {
    return api.onFreeDisplaysChanged(callback);
  } catch (err) {
    console.error("Failed to subscribe to free desktop displays:", logSafeError(err));
    return () => undefined;
  }
}

/**
 * Opens an app window on the display, or brings back the one already there.
 */
export async function openDesktopWindowOnDisplay(displayId: number): Promise<boolean> {
  const api = getElectronAPI();
  if (!api?.openWindowOnDisplay) return false;

  try {
    const result = await api.openWindowOnDisplay(displayId);
    if (!result.ok) console.error("Failed to open desktop window on display:", result.error);
    return result.ok;
  } catch (err) {
    console.error("Failed to open desktop window on display:", logSafeError(err));
    return false;
  }
}

/**
 * Get the Electron platform (darwin, win32, linux)
 * @returns platform string or null if not in Electron
 */
export async function getElectronPlatform(): Promise<string | null> {
  const api = getElectronAPI();
  if (!api) return null;
  
  try {
    return await api.getPlatform();
  } catch {
    // Silently fail - platform info is optional
    return null;
  }
}

/** The one macOS vibrancy the Frosted look uses, or null to clear it. */
export type VibrancyType = 'under-window' | null;

/**
 * Set window vibrancy (macOS only), the native blur behind the Frosted look.
 * @returns true if applied, false if not supported or failed
 */
export async function setVibrancy(vibrancy: VibrancyType): Promise<boolean> {
  const api = getElectronAPI();
  if (!api) return false;

  try {
    return await api.setVibrancy(vibrancy);
  } catch (err) {
    console.error('Failed to set vibrancy:', logSafeError(err));
    return false;
  }
}

/**
 * Turns the Wallpaper look on or off for this window. While on, the main
 * process pushes the window's position and the pictures under it.
 * @returns the current state, or null off the desktop, on older shells, or when turning off
 */
export async function subscribeDesktopWallpaper(enabled: boolean): Promise<DesktopWallpaperState | null> {
  const api = getElectronAPI();
  if (!api?.subscribeWallpaper) return null;

  try {
    return await api.subscribeWallpaper(enabled);
  } catch (err) {
    console.error('Failed to subscribe to the desktop wallpaper:', logSafeError(err));
    return null;
  }
}

/**
 * Tells the desktop shell this window's background is on screen, so the
 * window appears now rather than at the first paint of the bare page.
 */
export function reportSurfaceReady(): void {
  const api = getElectronAPI();
  if (!api?.reportSurfaceReady) return;

  try {
    api.reportSurfaceReady();
  } catch (err) {
    console.error('Failed to report the window background:', logSafeError(err));
  }
}

/** The picture for a layer key the main process issued, or null. */
export async function getDesktopWallpaperImage(key: string): Promise<DesktopWallpaperImage | null> {
  const api = getElectronAPI();
  if (!api?.getWallpaperImage) return null;

  try {
    return await api.getWallpaperImage(key);
  } catch (err) {
    console.error('Failed to load the desktop wallpaper:', logSafeError(err));
    return null;
  }
}

/** Calls back on every wallpaper push. Returns the unsubscribe function. */
export function onDesktopWallpaperChanged(callback: (state: DesktopWallpaperState) => void): () => void {
  const api = getElectronAPI();
  if (!api?.onWallpaperChanged) return () => undefined;

  try {
    return api.onWallpaperChanged(callback);
  } catch (err) {
    console.error('Failed to listen for desktop wallpaper changes:', logSafeError(err));
    return () => undefined;
  }
}

// ============================================
// FOLDER WATCHER UTILITIES
// ============================================

/**
 * Open a dialog to select a folder to watch
 * @returns The selected folder path, or null if cancelled
 */
export async function selectWatchFolder(): Promise<string | null> {
  const api = getElectronAPI();
  if (!api) return null;
  
  try {
    return await api.selectWatchFolder();
  } catch (err) {
    console.error('Failed to select watch folder:', logSafeError(err));
    return null;
  }
}

/**
 * Start watching a folder for new files
 * @param folderPath The path to watch
 * @param processExistingFiles If true, process all existing files in the folder first (for initial sync)
 * @returns true if successfully started
 */
export async function startFolderWatcher(folderPath: string, processExistingFiles: boolean = false): Promise<boolean> {
  const api = getElectronAPI();
  if (!api) return false;
  
  try {
    return await api.startFolderWatcher(folderPath, processExistingFiles);
  } catch (err) {
    console.error('Failed to start folder watcher:', logSafeError(err));
    return false;
  }
}

/**
 * Stop the folder watcher
 * @returns true if successfully stopped
 */
export async function stopFolderWatcher(): Promise<boolean> {
  const api = getElectronAPI();
  if (!api) return false;
  
  try {
    return await api.stopFolderWatcher();
  } catch (err) {
    console.error('Failed to stop folder watcher:', logSafeError(err));
    return false;
  }
}

/**
 * Get the current folder watcher state
 */
export async function getFolderWatcherState(): Promise<FolderWatcherState | null> {
  const api = getElectronAPI();
  if (!api) return null;
  
  try {
    return await api.getFolderWatcherState();
  } catch (err) {
    console.error('Failed to get folder watcher state:', logSafeError(err));
    return null;
  }
}

/**
 * Read the watched folder now and import supported files not yet imported.
 * @returns how many were found, or null when nothing is being watched or the
 * desktop build predates the scan.
 */
export async function scanWatchFolder(): Promise<number | null> {
  const api = getElectronAPI();
  if (!api?.scanWatchFolder) return null;

  try {
    return await api.scanWatchFolder();
  } catch (err) {
    console.error('Failed to scan watched folder:', logSafeError(err));
    return null;
  }
}

/**
 * Clear the processed files cache (allows re-uploading existing files)
 */
export async function clearProcessedFiles(): Promise<boolean> {
  const api = getElectronAPI();
  if (!api) return false;
  
  try {
    return await api.clearProcessedFiles();
  } catch (err) {
    console.error('Failed to clear processed files:', logSafeError(err));
    return false;
  }
}

/**
 * Subscribe to folder watcher file events
 * @param callback Called when a new file is detected
 * @returns Cleanup function to unsubscribe
 */
export function onFolderWatcherFile(callback: (fileInfo: WatchedFileInfo) => void): (() => void) | null {
  const api = getElectronAPI();
  if (!api) return null;
  
  return api.onFolderWatcherFile(callback);
}

/**
 * Subscribe to folder watcher error events
 * @param callback Called when an error occurs
 * @returns Cleanup function to unsubscribe
 */
export function onFolderWatcherError(callback: (error: FolderWatcherError) => void): (() => void) | null {
  const api = getElectronAPI();
  if (!api) return null;
  
  return api.onFolderWatcherError(callback);
}

/**
 * Convert a base64 string to a File object
 * Used to convert watched files to uploadable File objects
 */
export function base64ToFile(fileInfo: WatchedFileInfo): File {
  const byteCharacters = atob(fileInfo.base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.codePointAt(i) ?? 0;
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: fileInfo.mimeType });
  return new File([blob], fileInfo.name, { 
    type: fileInfo.mimeType,
    lastModified: fileInfo.lastModified 
  });
}

// ============================================
// OAUTH AUTHENTICATION POPUP
// ============================================

/**
 * Open OAuth authentication in a native Electron popup window
 * This supports WebAuthn/passkeys, unlike webview elements
 * 
 * @param authUrl The OAuth authorization URL to open
 * @returns Promise with the auth result (session on success, error on failure)
 */
export async function openAuthPopup(authUrl: string, codeVerifier: string): Promise<AuthPopupResult> {
  const api = getElectronAPI();
  if (!api) {
    return { success: false, error: 'not_electron' };
  }
  
  try {
    return await api.openAuthPopup(authUrl, codeVerifier);
  } catch (err) {
    console.error('Failed to open auth popup:', logSafeError(err));
    return { success: false, error: err instanceof Error ? err.message : 'unknown_error' };
  }
}

export async function openEmbeddedAuth(authUrl: string, codeVerifier: string): Promise<AuthPopupResult> {
  const api = getElectronAPI();
  if (!api) return { success: false, error: 'not_electron' };

  try {
    return await api.openEmbeddedAuth(authUrl, codeVerifier);
  } catch (err) {
    console.error('Failed to open embedded auth:', logSafeError(err));
    return { success: false, error: err instanceof Error ? err.message : 'unknown_error' };
  }
}

export function onEmbeddedAuthState(
  callback: (state: EmbeddedAuthState) => void,
): () => void {
  const api = getElectronAPI();
  return api?.onEmbeddedAuthState(callback) ?? (() => {});
}

export function onEmbeddedAuthFocusShell(callback: () => void): () => void {
  const api = getElectronAPI();
  return api?.onEmbeddedAuthFocusShell(callback) ?? (() => {});
}

/**
 * Desktop only: fires when the OS locks the screen or the device sleeps.
 * Older desktop builds without the bridge simply never fire.
 */
export function onSessionLockRequired(
  callback: (event: { reason: "lock-screen" | "suspend" }) => void,
): () => void {
  const api = getElectronAPI();
  return api?.onSessionLockRequired?.(callback) ?? (() => {});
}

/**
 * Close the auth popup if it's open
 * @returns true if popup was closed, false if no popup was open
 */
export async function closeAuthPopup(): Promise<boolean> {
  const api = getElectronAPI();
  if (!api) return false;
  
  try {
    return await api.closeAuthPopup();
  } catch (err) {
    console.error('Failed to close auth popup:', logSafeError(err));
    return false;
  }
}
